import { makeService } from '@upstart/shared-api-utils';
import { OkResponse, RequestBody } from 'util/api';
import { useTypedBackendClient } from './useTypedBackendClient';

export type GetDealerSetupFeatureConfigsResponse = OkResponse<
  '/v2/workflow/dealer-setup/{dealerId}/feature-configs',
  'get'
>['data'];

export type UpdateDealerSetupFeatureConfigsResponse = OkResponse<
  '/v2/workflow/dealer-setup/{dealerId}/feature-configs',
  'patch'
>['data'];
export type UpdateDealerSetupFeatureConfigsRequest = RequestBody<
  '/v2/workflow/dealer-setup/{dealerId}/feature-configs',
  'patch'
>;

export interface DealerSetupService {
  getFeatureConfigs({
    dealerId,
  }: {
    dealerId: string;
  }): Promise<GetDealerSetupFeatureConfigsResponse>;
  updateFeatureConfigs({
    dealerId,
    featureConfigs,
  }: {
    dealerId: string;
    featureConfigs: UpdateDealerSetupFeatureConfigsRequest;
  }): Promise<UpdateDealerSetupFeatureConfigsResponse>;
}

const useDealerSetupFns = (): DealerSetupService => {
  const { backendClient } = useTypedBackendClient();

  const getFeatureConfigs: DealerSetupService['getFeatureConfigs'] = async ({
    dealerId,
  }) => {
    const { data, error } = await backendClient.GET(
      '/v2/workflow/dealer-setup/{dealerId}/feature-configs',
      {
        params: {
          path: { dealerId },
        },
      },
    );
    if (error) {
      throw new Error(error.detail || error.title);
    }
    return data.data;
  };

  const updateFeatureConfigs: DealerSetupService['updateFeatureConfigs'] =
    async ({ dealerId, featureConfigs }) => {
      const { data, error } = await backendClient.PATCH(
        '/v2/workflow/dealer-setup/{dealerId}/feature-configs',
        {
          params: {
            path: { dealerId },
          },
          body: featureConfigs,
        },
      );
      if (error) {
        throw new Error(error.detail || error.title);
      }
      return data.data;
    };

  return {
    getFeatureConfigs,
    updateFeatureConfigs,
  };
};

const {
  OverridesProvider: DealerSetupServiceOverridesProvider,
  useService: useDealerSetupService,
} = makeService({
  name: 'DealerSetup',
  useServiceFns: useDealerSetupFns,
});

export { DealerSetupServiceOverridesProvider, useDealerSetupService };
